"use client"

import { useState, useCallback, useRef } from "react"
import { preloadHeroSprites, preloadArcherSprites, preloadMageSprites, preloadSiegeSprites, preloadTowerSprites, preloadSummonSprites, preloadEnemySprites, preloadTilesetImages } from "@/utils/rendering"
import { PHASE_1_ASSETS, PHASE_2_ASSETS, HERO_ASSETS, TOWER_AND_SUMMON_ASSETS, LEVEL_ASSETS, SHARED_ASSETS } from "./use-asset-preloader"

// Phase loading states
export type LoadPhase = 1 | 2 | 3 | 4
export type PhaseStatus = "idle" | "loading" | "complete" | "error"

interface PhaseState {
  status: PhaseStatus
  progress: number
  loaded: number
  total: number
  errors: string[]
}

type PhaseKey = "phase1" | "phase2" | "phase3" | "phase4"

interface PhasedPreloadState {
  phase1: PhaseState
  phase2: PhaseState
  phase3: PhaseState
  phase4: PhaseState
  currentPhase: LoadPhase | null
}

// Safety net so a stalled audio element (iOS low-power mode, media load caps)
// can never wedge the loading screen
const AUDIO_PRELOAD_TIMEOUT_MS = 8000

const createEmptyPhaseState = (): PhaseState => ({
  status: "idle",
  progress: 0,
  loaded: 0,
  total: 0,
  errors: [],
})

// Helper to flatten asset objects into arrays
const flattenAssets = (assets: Record<string, string[] | string>): string[] => {
  return Object.values(assets).flat().filter((asset): asset is string => typeof asset === "string")
}

// Get assets for Phase 3 based on selected hero
const getPhase3Assets = (selectedHero: "paladin" | "ranger" | "siegesmith", level = 1): string[] => {
  const heroSprites = HERO_ASSETS[selectedHero] || []
  const towerAssets = flattenAssets(TOWER_AND_SUMMON_ASSETS)
  const levelAssets = LEVEL_ASSETS[level] ? flattenAssets(LEVEL_ASSETS[level]) : []
  const sharedAssets = flattenAssets(SHARED_ASSETS)

  return [...heroSprites, ...towerAssets, ...levelAssets, ...sharedAssets]
}

// Get assets for Phase 4 (on-demand level loading)
const getPhase4Assets = (level: number): string[] => {
  const levelAssets = LEVEL_ASSETS[level]
  if (!levelAssets) return []
  return flattenAssets(levelAssets)
}

export const usePhasedAssetLoader = () => {
  const [state, setState] = useState<PhasedPreloadState>({
    phase1: createEmptyPhaseState(),
    phase2: createEmptyPhaseState(),
    phase3: createEmptyPhaseState(),
    phase4: createEmptyPhaseState(),
    currentPhase: null,
  })

  // Generic asset preloader
  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image()
      img.onload = () => resolve()
      img.onerror = () => reject(new Error(`Failed to load: ${src}`))
      img.src = src
    })
  }, [])

  const preloadAudio = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio()
      // canplaythrough is not guaranteed to fire (iOS Safari defers media
      // buffering) - treat a timeout as loaded rather than hanging forever
      const timeout = setTimeout(() => resolve(), AUDIO_PRELOAD_TIMEOUT_MS)
      audio.oncanplaythrough = () => {
        clearTimeout(timeout)
        resolve()
      }
      audio.onerror = () => {
        clearTimeout(timeout)
        reject(new Error(`Failed to load audio: ${src}`))
      }
      audio.src = src
      audio.load()
    })
  }, [])

  const isAudioFile = (src: string) => {
    return /\.(mp3|wav|ogg|m4a)$/i.test(src)
  }

  // Phases currently in flight or done - checked synchronously so two calls
  // in the same tick (e.g. StrictMode double-effects) can't both start loading
  const startedPhasesRef = useRef<Set<LoadPhase>>(new Set())

  // Load a specific phase
  const loadPhase = useCallback(
    async (phase: LoadPhase, assets: string[]) => {
      const phaseKey = `phase${phase}` as PhaseKey

      // Skip if already loading or complete
      if (startedPhasesRef.current.has(phase)) {
        return
      }
      startedPhasesRef.current.add(phase)

      const total = assets.length
      if (total === 0) {
        setState((prev) => ({
          ...prev,
          [phaseKey]: { ...prev[phaseKey], status: "complete", progress: 100 },
        }))
        return
      }

      // Mark as loading
      setState((prev) => ({
        ...prev,
        currentPhase: phase,
        [phaseKey]: { ...prev[phaseKey], status: "loading", total },
      }))

      let loadedCount = 0
      const errors: string[] = []

      const loadAsset = async (src: string) => {
        try {
          if (isAudioFile(src)) {
            await preloadAudio(src)
          } else {
            await preloadImage(src)
          }
        } catch {
          errors.push(src)
          console.warn(`[PhasedLoader] Phase ${phase}: Failed to load ${src}`)
        } finally {
          loadedCount++
          setState((prev) => ({
            ...prev,
            [phaseKey]: {
              ...prev[phaseKey],
              loaded: loadedCount,
              progress: Math.round((loadedCount / total) * 100),
              status: loadedCount === total ? "complete" : "loading",
              errors: [...errors],
            },
          }))
        }
      }

      // Load all assets in parallel
      await Promise.all(assets.map(loadAsset))

      // After Phase 3 completes, preload sprites into rendering cache
      if (phase === 3) {
        preloadHeroSprites()
        preloadArcherSprites()
        preloadMageSprites()
        preloadSiegeSprites()
        preloadTowerSprites()
        preloadSummonSprites()
        preloadEnemySprites()
        preloadTilesetImages()
      }

      setState((prev) => ({
        ...prev,
        currentPhase: null,
      }))
    },
    [preloadImage, preloadAudio]
  )

  // Public API for loading phases
  const loadPhase1 = useCallback(async () => {
    const assets = flattenAssets(PHASE_1_ASSETS)
    await loadPhase(1, assets)
  }, [loadPhase])

  const loadPhase2 = useCallback(async () => {
    const assets = flattenAssets(PHASE_2_ASSETS)
    await loadPhase(2, assets)
  }, [loadPhase])

  const loadPhase3 = useCallback(
    async (selectedHero: "paladin" | "ranger" | "siegesmith", level = 1) => {
      const assets = getPhase3Assets(selectedHero, level)
      await loadPhase(3, assets)
    },
    [loadPhase]
  )

  const loadPhase4 = useCallback(
    async (level: number) => {
      const assets = getPhase4Assets(level)
      await loadPhase(4, assets)
    },
    [loadPhase]
  )

  // Check if a phase is complete
  const isPhaseComplete = useCallback(
    (phase: LoadPhase): boolean => {
      const phaseKey = `phase${phase}` as PhaseKey
      return state[phaseKey].status === "complete"
    },
    [state]
  )

  return {
    state,
    loadPhase1,
    loadPhase2,
    loadPhase3,
    loadPhase4,
    isPhaseComplete,
  }
}
